import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  ResponsiveContainer,
  ComposedChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Bar,
  Line,
} from "recharts";
import { ChevronLeft, ChevronRight } from "lucide-react";

export interface AssignmentProgressProps {
  data: Array<{
    title: string;
    averageScore: number;
    completionRate: number;
    submissionDate: string;
    submissions: number;
  }>;
  isFullScreen?: boolean;
}

const TruncatedTick: React.FC<any> = ({ x, y, payload }) => {
  const maxLength = 17;
  const displayValue =
    payload.value.length > maxLength
      ? payload.value.slice(0, maxLength) + "…"
      : payload.value;

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={16}
        textAnchor="end"
        fill="#6B7280"
        fontSize={12}
        transform="rotate(-45)"
      >
        {displayValue}
      </text>
    </g>
  );
};

export const AssignmentProgressChart: React.FC<AssignmentProgressProps> = ({
  data,
  isFullScreen = false,
}) => {
  const { t } = useTranslation();
  const [itemsToShow, setItemsToShow] = useState(5);
  const [currentIndex, setCurrentIndex] = useState(0);

  const getMetricTranslation = (name: string) => {
    switch (name) {
      case "averageScore":
        return t("analytics.metrics.averageScore");
      case "completionRate":
        return t("analytics.metrics.completionRate");
      default:
        return name;
    }
  };

  // Gösterilecek veriyi hesapla
  const displayData = useMemo(() => {
    return data.slice(currentIndex, currentIndex + itemsToShow);
  }, [data, currentIndex, itemsToShow]);

  // Navigasyon kontrolleri
  const canGoLeft = currentIndex > 0;
  const canGoRight = currentIndex + itemsToShow < data.length;

  const goLeft = () => {
    if (canGoLeft) {
      setCurrentIndex(Math.max(0, currentIndex - 1));
    }
  };

  const goRight = () => {
    if (canGoRight) {
      setCurrentIndex(Math.min(data.length - itemsToShow, currentIndex + 1));
    }
  };

  // Filtre değişikliği
  const handleItemsToShowChange = (newValue: number) => {
    setItemsToShow(newValue);
    // Eğer yeni değer mevcut index'den büyükse index'i sıfırla
    if (currentIndex + newValue > data.length) {
      setCurrentIndex(Math.max(0, data.length - newValue));
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
      {/* Başlık */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {t("analytics.charts.assignments")}
        </h3>
      </div>

      {/* Filtre Bölümü */}
      {data && data.length > 0 && (
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t("analytics.charts.itemsToShow") || "Gösterilecek Sınav Sayısı"}
              :
            </label>
            <select
              value={itemsToShow}
              onChange={(e) => handleItemsToShowChange(Number(e.target.value))}
              className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value={3}>3</option>
              <option value={5}>5</option>
              <option value={7}>7</option>
              <option value={10}>10</option>
            </select>
          </div>

          {/* Navigasyon Kontrolleri */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {currentIndex + 1}-
              {Math.min(currentIndex + itemsToShow, data.length)} /{" "}
              {data.length}
            </span>
            <button
              onClick={goLeft}
              disabled={!canGoLeft}
              className={`p-1 rounded-md transition-colors ${
                canGoLeft
                  ? "bg-blue-500 hover:bg-blue-600 text-white"
                  : "bg-gray-200 dark:bg-gray-600 text-gray-400 cursor-not-allowed"
              }`}
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={goRight}
              disabled={!canGoRight}
              className={`p-1 rounded-md transition-colors ${
                canGoRight
                  ? "bg-blue-500 hover:bg-blue-600 text-white"
                  : "bg-gray-200 dark:bg-gray-600 text-gray-400 cursor-not-allowed"
              }`}
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      )}

      {/* Grafik */}
      {data && data.length > 0 ? (
        <div className={`${!isFullScreen ? "h-96" : "h-[55vh]"}`}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={displayData}
              margin={{ top: 20, right: 20, left: 0, bottom: 60 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                className="stroke-gray-200 dark:stroke-gray-700"
              />
              <XAxis
                dataKey="title"
                className="text-gray-600 dark:text-gray-300"
                angle={isFullScreen ? -45 : 0}
                textAnchor={isFullScreen ? "end" : "middle"}
                height={isFullScreen ? 70 : 10}
                interval={0}
                tick={isFullScreen ? <TruncatedTick /> : false}
              />

              <YAxis
                yAxisId="score"
                className="text-gray-600 dark:text-gray-300"
                tickFormatter={(value) => `${Math.round(value)}`}
                width={60}
                domain={[0, 100]}
                ticks={[0, 25, 50, 75, 100]}
              />
              <YAxis
                yAxisId="rate"
                orientation="right"
                className="text-gray-600 dark:text-gray-300"
                tickFormatter={(value) => `${Math.round(value)}%`}
                width={60}
                domain={[0, 100]}
                ticks={[0, 25, 50, 75, 100]}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  color: "#111827",
                  padding: "12px",
                }}
                formatter={(value: number, name: string) => {
                  if (name === "completionRate") {
                    return [
                      `${Math.round(value)}%`,
                      getMetricTranslation(name),
                    ];
                  }
                  return [`${Math.round(value)}`, getMetricTranslation(name)];
                }}
                labelFormatter={(label) => label}
              />
              <Legend
                verticalAlign="top"
                height={36}
                formatter={(value) => getMetricTranslation(value)}
              />
              <Bar
                yAxisId="score"
                dataKey="averageScore"
                fill="#3b82f6"
                name="averageScore"
                radius={[4, 4, 0, 0]}
              />
              <Line
                yAxisId="rate"
                type="monotone"
                dataKey="completionRate"
                stroke="#22c55e"
                strokeWidth={2}
                name="completionRate"
                dot={{ fill: "#22c55e", strokeWidth: 2, r: 4 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="flex items-center justify-center h-48 text-gray-500 dark:text-gray-400">
          <div className="text-center">
            <p className="text-lg font-medium">
              {t("analytics.charts.noData") || "Veri bulunamadı"}
            </p>
            <p className="text-sm mt-1">
              {t("analytics.charts.noDataDescription") ||
                "Görüntülenecek sınav verisi bulunmuyor."}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignmentProgressChart;
